import type { OibControlSummary, OibInstrumentSummary, OibRoleSummary, OibSceneSummary, OibStateSummary } from './types';

export interface OibLoadResult {
  readonly ok: boolean;
  readonly value?: OibInstrumentSummary;
  readonly errors: readonly string[];
}

const SUPPORTED_PREFIX = 'oib.instrument/';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Liest eine oib.instrument/-Definition rein lesend ein und extrahiert nur
 * die fuer OSAL relevanten Felder. Unbekannte oder fehlerhafte Eintraege
 * werden uebersprungen statt die gesamte Datei abzulehnen - OSAL ist kein
 * Ersatz fuer die OIB-eigene Validierung.
 */
export function loadOibInstrumentSummary(input: unknown): OibLoadResult {
  const errors: string[] = [];
  if (!isRecord(input)) {
    return { ok: false, errors: ['Die Datei ist kein JSON-Objekt.'] };
  }
  const schemaVersion = input['schemaVersion'];
  if (typeof schemaVersion !== 'string' || !schemaVersion.startsWith(SUPPORTED_PREFIX)) {
    return { ok: false, errors: [`Unbekannte oder fehlende schemaVersion (erwartet "${SUPPORTED_PREFIX}*").`] };
  }
  const id = typeof input['id'] === 'string' ? input['id'] : '';
  const name = typeof input['name'] === 'string' ? input['name'] : '';
  if (id === '') errors.push('Feld "id" fehlt oder ist kein String.');
  if (name === '') errors.push('Feld "name" fehlt oder ist kein String.');

  const roles = extractRoles(input['roles'], errors);
  const state = extractState(input['state'], errors);
  const controls = extractControls(input['controls'], errors);
  const scenes = extractScenes(input['scenes'], errors);

  const value: OibInstrumentSummary = { schemaVersion, id, name, roles, state, controls, scenes };
  return { ok: true, value, errors };
}

function extractRoles(raw: unknown, errors: string[]): readonly OibRoleSummary[] {
  if (!Array.isArray(raw)) {
    errors.push('Feld "roles" fehlt oder ist kein Array - keine Rollen verfuegbar.');
    return [];
  }
  const result: OibRoleSummary[] = [];
  raw.forEach((entry, index) => {
    if (!isRecord(entry) || typeof entry['id'] !== 'string') {
      errors.push(`roles[${index}] uebersprungen: keine gueltige id.`);
      return;
    }
    const kind = entry['kind'] === 'control' ? 'control' : 'note';
    const timbre = typeof entry['timbre'] === 'string' ? entry['timbre'] : undefined;
    result.push({
      id: entry['id'],
      name: typeof entry['name'] === 'string' ? entry['name'] : entry['id'],
      kind,
      ...(timbre !== undefined ? { timbre } : {}),
    });
  });
  return result;
}

function extractState(raw: unknown, errors: string[]): readonly OibStateSummary[] {
  if (!Array.isArray(raw)) {
    errors.push('Feld "state" fehlt oder ist kein Array - keine Zustaende verfuegbar.');
    return [];
  }
  const result: OibStateSummary[] = [];
  raw.forEach((entry, index) => {
    if (!isRecord(entry) || typeof entry['id'] !== 'string' || typeof entry['type'] !== 'string') {
      errors.push(`state[${index}] uebersprungen: keine gueltige id/type.`);
      return;
    }
    const type = entry['type'];
    if (type !== 'boolean' && type !== 'number' && type !== 'string' && type !== 'enum') {
      errors.push(`state[${index}] uebersprungen: unbekannter type "${type}".`);
      return;
    }
    const values = Array.isArray(entry['values']) ? entry['values'].filter((v): v is string => typeof v === 'string') : undefined;
    const min = typeof entry['min'] === 'number' ? entry['min'] : undefined;
    const max = typeof entry['max'] === 'number' ? entry['max'] : undefined;
    result.push({
      id: entry['id'],
      type,
      ...(values !== undefined ? { values } : {}),
      ...(min !== undefined ? { min } : {}),
      ...(max !== undefined ? { max } : {}),
    });
  });
  return result;
}

function extractControls(raw: unknown, errors: string[]): readonly OibControlSummary[] {
  if (!Array.isArray(raw)) {
    errors.push('Feld "controls" fehlt oder ist kein Array - keine Bedienelemente verfuegbar.');
    return [];
  }
  const result: OibControlSummary[] = [];
  raw.forEach((entry, index) => {
    if (!isRecord(entry) || typeof entry['id'] !== 'string') {
      errors.push(`controls[${index}] uebersprungen: keine gueltige id.`);
      return;
    }
    result.push({ id: entry['id'], type: typeof entry['type'] === 'string' ? entry['type'] : 'unknown' });
  });
  return result;
}

function extractScenes(raw: unknown, errors: string[]): readonly OibSceneSummary[] {
  if (raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    errors.push('Feld "scenes" ist kein Array - keine Szenen verfuegbar.');
    return [];
  }
  const result: OibSceneSummary[] = [];
  raw.forEach((entry, index) => {
    if (!isRecord(entry) || typeof entry['id'] !== 'string') {
      errors.push(`scenes[${index}] uebersprungen: keine gueltige id.`);
      return;
    }
    result.push({ id: entry['id'], name: typeof entry['name'] === 'string' ? entry['name'] : entry['id'] });
  });
  return result;
}
